import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../services/api';
import type { EsitoRinnovo, ApiRequestError } from '../services/api';
import { setSocketAuthToken, getSocket, setSocketTokenRefresher, setSocketIdentitaAttesa } from '../services/socket';
import type { User, RegisterInput, LoginInput, UpgradeInput } from '../types/auth';
import { getRatingFieldName } from '../utils/rating';
import { trackRegistration, trackLogin, trackGuestUpgrade, trackLoginFailed, trackRegistrationFailed } from '../utils/analytics';
import { log } from '../utils/logger';
import { useReviewStore } from './review-store';

let tokenRefreshCallbackRegistered = false;

interface AuthStore {
  // State
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isAuthReady: boolean;
  isGuest: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  /**
   * Verifica l'indirizzo e APRE LA SESSIONE con quello che il server consegna.
   * Vive qui e non nella schermata perche' e' una porta d'ingresso come
   * `login`: il gettone d'accesso si registra in un posto solo.
   */
  verificaEmail: (token: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  upgradeGuest: (input: UpgradeInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  /**
   * Lo stesso rinnovo, ma con l'esito grezzo. Serve a chi deve distinguere
   * «il token e' morto» da «la rete non ha risposto» — cioe' `checkAuth()`.
   * `refreshToken()` resta booleano per tutti gli altri chiamanti: qui
   * `'transitorio'` e' una stringa truthy, e un `if (await ...)` la leggerebbe
   * come riuscita.
   */
  rinnovaSessione: () => Promise<EsitoRinnovo>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
  updateRating: (newRating: number, variant?: string, timeControl?: string) => void;
  updateAvatar: (avatar: string | null) => Promise<void>;
  updateCountry: (country: string | null) => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isAuthReady: false,
      isGuest: false,
      isLoading: false,
      error: null,

      login: async (input: LoginInput) => {
        log.info('Auth', 'login() called', { identifier: input.identifier });
        set({ isLoading: true, error: null });
        try {
          const response = await api.login(input);
          api.setAccessToken(response.tokens.accessToken);
          set({
            user: response.user,
            accessToken: response.tokens.accessToken,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
          });
          log.info('Auth', 'login() success', { userId: response.user.id, username: response.user.username });
          trackLogin('credentials');
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'Login failed';
          log.error('Auth', 'login() failed', error);
          trackLoginFailed(reason);
          set({
            isLoading: false,
            error: reason,
          });
          throw error;
        }
      },

      register: async (input: RegisterInput) => {
        log.info('Auth', 'register() called', { username: input.username });
        set({ isLoading: true, error: null });
        try {
          await api.register(input);
          set({ isLoading: false });
          log.info('Auth', 'register() success — awaiting email verification');
          trackRegistration();
          // Don't set user/token — user must verify email first
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'Registration failed';
          log.error('Auth', 'register() failed', error);
          trackRegistrationFailed(reason);
          set({
            isLoading: false,
            error: reason,
          });
          throw error;
        }
      },

      /*
       * La verifica dell'email e' la TERZA porta d'ingresso, accanto a `login` e
       * `upgradeGuest`, e fa le stesse identiche tre cose: consegna il gettone
       * d'accesso alla porta delle richieste, registra utente e gettone nello
       * stato, dichiara la sessione non-ospite. Scriverle nella schermata di
       * verifica avrebbe voluto dire una seconda copia di quel rito, e la seconda
       * copia e' quella che il giorno del prossimo campo in piu' nessuno aggiorna.
       *
       * Il messaggio del server NON torna al chiamante: e' in inglese per tutti, e
       * la schermata ha la sua stringa tradotta.
       */
      verificaEmail: async (token: string) => {
        log.info('Auth', 'verificaEmail() called');
        set({ isLoading: true, error: null });
        try {
          const risposta = await api.verifyEmail(token);
          api.setAccessToken(risposta.tokens.accessToken);
          set({
            user: risposta.user,
            accessToken: risposta.tokens.accessToken,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
          });
          log.info('Auth', 'verificaEmail() success', { userId: risposta.user.id });
        } catch (error) {
          const motivo = error instanceof Error ? error.message : 'Verification failed';
          log.error('Auth', 'verificaEmail() failed', error);
          set({ isLoading: false, error: motivo });
          throw error;
        }
      },

      loginAsGuest: async () => {
        log.info('Auth', 'loginAsGuest() called');
        set({ isLoading: true, error: null });
        try {
          const response = await api.guestLogin();
          api.setAccessToken(response.tokens.accessToken);
          set({
            user: response.user,
            accessToken: response.tokens.accessToken,
            isAuthenticated: true,
            isGuest: true,
            isLoading: false,
          });
          log.info('Auth', 'loginAsGuest() success', { userId: response.user.id, username: response.user.username });
          trackLogin('guest');
        } catch (error) {
          /*
           * «Sei gia' un utente registrato» non e' un errore: e' la sessione che
           * c'e' gia', e si entra con quella.
           *
           * Il server rifiuta l'ospite quando il browser porta il cookie di una
           * sessione registrata VIVA (`handleGuestLogin`). Il client pero' puo'
           * non saperlo: all'avvio rinnova solo se la memoria locale dice «ero
           * loggato», e l'app installata su iPhone parte con la memoria vuota e
           * i cookie copiati da Safari. Seralanc, 26 settembre: tre «Gioca come
           * ospite» respinti con un errore generico, poi il limitatore, poi la
           * password a mano.
           *
           * Se il rinnovo o il profilo non arrivano, si ricade sull'errore di
           * prima: nessuna strada nuova per restare a meta'.
           */
          if ((error as ApiRequestError | null)?.code === 'ALREADY_AUTHENTICATED') {
            log.info('Auth', 'loginAsGuest(): sessione registrata gia viva, entro con quella');
            if (await get().rinnovaSessione() === 'riuscito') {
              try {
                const response = await api.getMe();
                set({
                  user: response.user,
                  isAuthenticated: true,
                  isGuest: response.user.isGuest ?? false,
                  isLoading: false,
                  error: null,
                });
                log.info('Auth', 'loginAsGuest(): ripresa la sessione registrata', { userId: response.user.id, username: response.user.username });
                return;
              } catch (erroreProfilo) {
                log.warn('Auth', 'loginAsGuest(): profilo non caricato dopo il rinnovo', { error: erroreProfilo instanceof Error ? erroreProfilo.message : String(erroreProfilo) });
              }
            }
          }
          log.error('Auth', 'loginAsGuest() failed', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Guest login failed',
          });
          throw error;
        }
      },

      upgradeGuest: async (input: UpgradeInput) => {
        log.info('Auth', 'upgradeGuestAccount() called', { username: input.username });
        set({ isLoading: true, error: null });
        try {
          const response = await api.upgradeGuest(input);
          if ('tokens' in response) {
            api.setAccessToken(response.tokens.accessToken);
            set({
              user: response.user,
              accessToken: response.tokens.accessToken,
              isAuthenticated: true,
              isGuest: false,
              isLoading: false,
            });
            log.info('Auth', 'upgradeGuestAccount() success', { userId: response.user.id });
            trackGuestUpgrade();
          } else {
            // Email verification required — clear guest session
            log.info('Auth', 'upgradeGuestAccount() requires email verification', { discardedGuestId: get().user?.id });
            api.setAccessToken(null);
            set({
              user: null,
              accessToken: null,
              isAuthenticated: false,
              isGuest: false,
              isLoading: false,
            });
            throw new Error('Please check your email to verify your account');
          }
        } catch (error) {
          log.error('Auth', 'upgradeGuestAccount() failed', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Account upgrade failed',
          });
          throw error;
        }
      },

      logout: async () => {
        log.info('Auth', 'logout() called');
        // FIX-43: Notify server to clean up matchmaking/arena/swiss queues before
        // the HTTP logout call so Redis is never left with stale entries.
        const socket = getSocket();
        if (socket?.connected) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              log.warn('Auth', 'auth:logout socket ack timed out (1.5s) — Redis queues may be stale');
              resolve();
            }, 1500);
            socket.emit('auth:logout', () => {
              log.info('Auth', 'auth:logout socket ack received');
              clearTimeout(timeout);
              resolve();
            });
          });
        }
        try {
          await api.logout();
        } catch (error) {
          // Logout API failed — server session may persist
          log.warn('Auth', 'logout API failed (server session may persist)', { error: error instanceof Error ? error.message : String(error) });
        } finally {
          api.setAccessToken(null);
          // The review library is per-user and cached in memory: drop it, or the
          // next account to log in would briefly see the previous user's folders.
          useReviewStore.getState().reset();
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isGuest: false,
          });
        }
      },

      refreshToken: async () => {
        return (await get().rinnovaSessione()) === 'riuscito';
      },

      rinnovaSessione: async () => {
        // Use the deduplicated tryRefresh() from ApiClient.
        // This ensures that if the 401 interceptor is already refreshing,
        // we piggyback on the same promise instead of firing a second
        // request with the already-revoked refresh token.
        const esito = await api.rinnovaToken();
        if (esito === 'credenziali-morte') {
          // Il server ha rifiutato il refresh token (401/403): e' scaduto o
          // revocato, e nessun ritentativo lo riporta in vita. Qui si slogga.
          log.warn('Auth', 'refreshToken: credenziali morte — clearing auth state');
          api.setAccessToken(null);
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isGuest: false,
          });
        } else if (esito === 'transitorio' || esito === 'rallenta') {
          /*
           * Server giu' o rete irraggiungibile. Lo stato di autenticazione
           * resta intatto: sloggiare qui butterebbe fuori dall'account chi ha
           * solo avuto un secondo di rete ballerina — lo scenario del rientro
           * da app in secondo piano, a partita in corso.
           *
           * Il 429 finisce qui insieme al transitorio, e per la stessa ragione:
           * il rate limiter non dice niente sulle credenziali. Cambia solo per
           * chi RITENTA — e chi ritenta e' il socket, non questo posto.
           */
          log.warn('Auth', 'refreshToken: fallimento transitorio — stato di autenticazione intatto');
        }
        // Note: on success, the token is already set by doRefresh() and
        // the onTokenRefreshed callback updates the store's accessToken.
        return esito;
      },

      clearError: () => set({ error: null }),

      updateRating: (newRating: number, variant?: string, timeControl?: string) => {
        const user = get().user;
        if (!user) return;

        if (variant && timeControl) {
          const field = getRatingFieldName(variant, timeControl);
          if (field) {
            const oldValue = user[field];
            log.info('Auth', 'updateRating', { field, oldValue, newRating, variant, timeControl });
            set({ user: { ...user, [field]: newRating } });
            return;
          }
          log.warn('Auth', 'updateRating: no field found', { variant, timeControl });
        }
        // Fallback: update legacy global rating
        log.info('Auth', 'updateRating fallback', { oldRating: user.rating, newRating, variant, timeControl });
        set({ user: { ...user, rating: newRating } });
      },

      updateAvatar: async (avatar: string | null) => {
        const { avatar: saved } = await api.updateAvatar(avatar);
        const user = get().user;
        if (user) {
          set({ user: { ...user, avatar: saved } });
        }
        log.info('Auth', 'updateAvatar', { avatar: saved });
      },

      updateCountry: async (country: string | null) => {
        const { country: saved } = await api.updateCountry(country);
        const user = get().user;
        if (user) {
          set({ user: { ...user, country: saved } });
        }
        log.info('Auth', 'updateCountry', { country: saved });
      },

      checkAuth: async () => {
        log.info('Auth', 'checkAuth() called');
        const { accessToken } = get();

        // Register callback for auto-refresh only once to avoid stacking
        if (!tokenRefreshCallbackRegistered) {
          tokenRefreshCallbackRegistered = true;
          api.setOnTokenRefreshed((newToken) => {
            set({ accessToken: newToken });
            // Update socket auth so reconnections use the fresh token
            setSocketAuthToken(newToken);
          });
          /*
           * Come il socket si procura un token fresco quando sta per
           * riconnettersi con uno scaduto. Non puo' importare questo store
           * (ciclo: auth-store -> services/socket -> auth-store), quindi la
           * funzione gliela si consegna.
           *
           * NON `get().refreshToken()`: quello, se fallisce, cancella lo stato
           * di autenticazione — e qui il fallimento piu' frequente e' proprio
           * il blip di rete di chi sta rientrando in partita.
           */
          setSocketTokenRefresher(async () => {
            const rinnovato = await api.refreshTokenRequest();
            return rinnovato ? get().accessToken : null;
          });
          /*
           * E se un'identita' ce la aspettiamo, anche quando il token in
           * memoria non c'e'. `accessToken` non e' persistito: al boot un
           * utente registrato ha `isAuthenticated: true` e `accessToken: null`,
           * e il socket senza questa risposta nasceva anonimo e ci restava —
           * il rinnovo si autoescludeva perche' «non c'e' nessun token da
           * rinnovare». Vedi `setSocketIdentitaAttesa` in services/socket.
           */
          setSocketIdentitaAttesa(() => get().isAuthenticated);
        }

        if (!accessToken) {
          // If persisted state says authenticated but no token, try refresh
          if (get().isAuthenticated) {
            const esito = await get().rinnovaSessione();
            if (esito === 'riuscito') {
              log.info('Auth', 'checkAuth() token refreshed successfully');
              try {
                const response = await api.getMe();
                log.info('Auth', 'checkAuth() user loaded', { userId: response.user.id, username: response.user.username });
                set({ user: response.user, isAuthenticated: true, isGuest: response.user.isGuest ?? false, isAuthReady: true });
              } catch (err) {
                log.warn('Auth', 'checkAuth() getMe failed after refresh — clearing auth (silent boot-time logout)', { error: err instanceof Error ? err.message : String(err) });
                api.setAccessToken(null);
                set({ user: null, accessToken: null, isAuthenticated: false, isGuest: false, isAuthReady: true });
              }
            } else if (esito === 'credenziali-morte') {
              /*
               * `rinnovaSessione()` ha gia' pulito lo stato: qui resta solo da
               * sbloccare l'app, altrimenti si ferma sul caricamento.
               */
              set({ isAuthReady: true });
            } else {
              /*
               * Rete assente, server giu' o 429. L'access token NON e'
               * persistito (vedi `partialize`), quindi QUESTO ramo e' il
               * percorso d'ingresso piu' frequente che esista: sloggiare qui
               * vuol dire buttare fuori chiunque apra l'app con un secondo di
               * rete ballerina.
               *
               * La sessione resta in piedi e guarisce da sola: l'intercettore
               * dei 401 rinnova a ogni chiamata API. E se il refresh token e'
               * davvero morto risponde 401, viene classificato
               * `credenziali-morte` e slogga come sopra — niente zombie.
               */
              log.warn('Auth', 'checkAuth() rinnovo transitorio all avvio — sessione tenuta in piedi', { esito });
              set({ isAuthReady: true });
            }
          } else {
            set({ isAuthReady: true });
          }
          return;
        }

        api.setAccessToken(accessToken);
        try {
          const response = await api.getMe();
          log.info('Auth', 'checkAuth() user loaded', { userId: response.user.id, username: response.user.username });
          set({ user: response.user, isAuthenticated: true, isGuest: response.user.isGuest ?? false, isAuthReady: true });
        } catch (error) {
          // Try to refresh
          const esito = await get().rinnovaSessione();
          if (esito === 'riuscito') {
            log.info('Auth', 'checkAuth() token refreshed successfully');
            try {
              const response = await api.getMe();
              log.info('Auth', 'checkAuth() user loaded after refresh', { userId: response.user.id, username: response.user.username });
              set({ user: response.user, isAuthenticated: true, isGuest: response.user.isGuest ?? false, isAuthReady: true });
            } catch (retryError) {
              log.warn('Auth', 'checkAuth() getMe failed after refresh retry — clearing auth', { error: retryError instanceof Error ? retryError.message : String(retryError) });
              api.setAccessToken(null);
              set({ user: null, accessToken: null, isAuthenticated: false, isGuest: false, isAuthReady: true });
            }
          } else if (esito === 'credenziali-morte') {
            log.warn('Auth', 'checkAuth() refresh rifiutato dal server — clearing auth');
            set({ isAuthReady: true });
          } else {
            /*
             * Stessa ragione del ramo d'avvio: server giu' o rete caduta non
             * dicono niente sul token. Si tiene la sessione e si lascia che
             * l'intercettore dei 401 la rinnovi alla prossima chiamata.
             */
            log.warn('Auth', 'checkAuth() rinnovo transitorio — sessione tenuta in piedi', { esito });
            set({ isAuthReady: true });
          }
        }
      },
    }),
    {
      name: 'draughts-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest,
      }),
    }
  )
);
